import React from 'react';

import Typography from './Typography';

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
	maxWidth?:
		| 'sm'
		| 'md'
		| 'lg'
		| 'xl'
		| '2xl'
		| '3xl'
		| '4xl'
		| '5xl'
		| '6xl'
		| '7xl'
		| 'full';
	padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
	center?: boolean;
	children: React.ReactNode;
}

const Container: React.FC<ContainerProps> = ({
	maxWidth = 'lg',
	padding = 'md',
	center = true,
	className = '',
	children,
	...props
}) => {
	// Max width styles
	const maxWidthStyles = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-lg',
		xl: 'max-w-xl',
		'2xl': 'max-w-2xl',
		'3xl': 'max-w-3xl',
		'4xl': 'max-w-4xl',
		'5xl': 'max-w-5xl',
		'6xl': 'max-w-6xl',
		'7xl': 'max-w-[1280px]',
		full: 'max-w-full',
	} as const;

	// Padding styles using standard Tailwind classes
	const paddingStyles = {
		none: '',
		sm: 'px-4 py-2',
		md: 'px-4 sm:px-6 lg:px-8 py-6',
		lg: 'px-6 sm:px-8 lg:px-12 py-8',
		xl: 'px-8 sm:px-12 lg:px-16 py-12',
	};

	return (
		<div
			className={`
        font-primary flex flex-col w-full
        ${maxWidthStyles[maxWidth]}
        ${paddingStyles[padding]}
        ${center ? 'mx-auto' : ''}
        ${className}
      `
				.trim()
				.replace(/\s+/g, ' ')}
			{...props}
		>
			{children}
		</div>
	);
};

export default Container;

// Page layout components
interface PageHeaderProps extends Omit<
	React.HTMLAttributes<HTMLDivElement>,
	'title'
> {
	title?: React.ReactNode;
	subtitle?: React.ReactNode;
	actions?: React.ReactNode;
	maxWidthClass?: string;
	variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'title';
}

export const PageHeader: React.FC<PageHeaderProps> = ({
	title,
	subtitle,
	actions,
	children,
	className = '',
	maxWidthClass = 'max-w-[1440px]',
	variant = 'h3',
	...props
}) => (
	<header
		className={`font-primary ${className}`.trim().replace(/\s+/g, ' ')}
		{...props}
	>
		<div
			className={`mx-auto flex w-full ${maxWidthClass} items-center justify-between gap-6 px-8 py-6`}
		>
			{children ? (
				children
			) : (
				<>
					<div className="flex flex-col gap-2">
						{title && (
							<Typography variant={variant} className="text-neutral-900">
								{title}
							</Typography>
						)}
						{subtitle && (
							<Typography variant="body1" color="muted">
								{subtitle}
							</Typography>
						)}
					</div>
					{actions && <div className="flex-shrink-0">{actions}</div>}
				</>
			)}
		</div>
	</header>
);

export const PageContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div
		className={`flex-1 w-full max-w-[1440px] mx-auto space-y-6 font-primary px-8 pb-16 ${className}`}
		{...props}
	>
		{children}
	</div>
);

export const Section: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div
		className={`
        bg-white rounded-lg
        shadow-sm border border-gray-200
        p-6 font-primary ${className}
    `
			.trim()
			.replace(/\s+/g, ' ')}
		{...props}
	>
		{children}
	</div>
);
